import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Addon } from '~/api/models/Addons';
import type { Feature } from '~/api/models/Features';

/** Which shell the auth screens render in. See views/login/Layout.vue. */
export type LoginStyle = 'legacy' | 'rich';

export interface Branding {
    name: string;
    logo?: string;
    login_logo?: string;
    favicon?: string;
    login_style?: LoginStyle;
}

export interface WHMCS {
    enabled: boolean;
    button_text: string;

    // Admin-only
    url?: string;
    client_id?: string;
    client_secret?: string;
    api_id?: string;
    api_secret?: string;
}

export interface SMTP {
    enabled?: boolean;
    host?: string;
    port?: number;
    encryption?: string;
    username?: string;
    password?: string;
    from_address?: string;
    from_name?: string;
}

export interface MailTheme {
    logo_width?: string | number;
    accent_color?: string;
    background_color?: string;
    content_color?: string;
    footer_text?: string;
}

export interface OpenGraph {
    site_name?: string;
    title?: string;
    description?: string;
    color?: string;
    image?: string;
}

export interface Injector {
    css?: string;
    js?: string;
}

export interface Misc {
    required_2fa?: number;
    graphs_show_max_resource_limit?: boolean;
    gravatar_enabled?: boolean;
    passkey_login_enabled?: boolean;

    // Admin-only
    databases_allow_random?: boolean;
    game_switcher_enabled?: boolean;
    hide_admin_audit_logs_from_users?: boolean;
}

export interface Pusher {
    namespace?: string;
    key?: string;
    cluster?: string;
}

export interface Hytale {
    profile_uuid?: string | null;
    profile_username?: string;
}

export interface FirewallProvider {
    type: string;
    prefixes: string[];
    url?: string;
    api_key?: string;
}

export interface Firewall {
    providers: FirewallProvider[];
}

export interface Billing {
    /** This instance's page in the billing dashboard. Admin-only. */
    instance_url?: string | null;
    /** Where to change plan. Admin-only. */
    plan_url?: string | null;
}

export interface Deployment {
    strategy?: string;
}

export interface Settings {
    default_locale?: string;

    branding?: Branding;
    whmcs?: WHMCS;
    smtp?: SMTP;
    mail_theme?: MailTheme;
    og?: OpenGraph;
    injector?: Injector;
    misc?: Misc;
    pusher?: Pusher;
    hytale?: Hytale;
    firewall?: Firewall;
    deployment?: Deployment;
    billing?: Billing | null;

    expired?: boolean;

    // When the panel locks for an overdue subscription. Admin-only, and only
    // sent while the lock is still pending.
    subscription_locks_at?: string | null;

    features: Record<string, Feature>;
    addons: Addon[];
    egg_specific_tags?: EggSpecificTag[];
}

export interface EggSpecificTag {
    slug: string;
    name: string;
    color: string;
    gamedig: string | null;
}

export const useSettingsStore = defineStore('settings', () => {
    // State
    const initialized = ref<boolean>(false);
    const data = ref<Settings | undefined>(undefined);

    // Getters
    const isInitialized = computed(() => initialized.value);
    const settings = computed(() => data.value);
    const branding = computed(() => data.value?.branding);
    const features = computed(() => data.value?.features || {});
    const addons = computed(() => data.value?.addons || {});
    const eggSpecificTags = computed(() => data.value?.egg_specific_tags || []);
    // Whether a slug is a special game tag (drives game-specific features). Reactive
    // so it can light up a live-edited slug field as the user types.
    const isSpecialTagSlug = computed(() => (slug: string) => eggSpecificTags.value.some((entry) => entry.slug === slug));
    const whmcs = computed(() => data.value?.whmcs);
    const smtp = computed(() => data.value?.smtp);
    const mailTheme = computed(() => data.value?.mail_theme);
    const openGraph = computed(() => data.value?.og);
    const injector = computed(() => data.value?.injector);
    const misc = computed(() => data.value?.misc);
    const pusher = computed(() => data.value?.pusher);
    const defaultLocale = computed(() => data.value?.default_locale);
    const isExpired = computed(() => data.value?.expired || false);
    const subscriptionLocksAt = computed(() => {
        const locksAt = data.value?.subscription_locks_at;

        return locksAt ? new Date(locksAt) : undefined;
    });

    // Feature helpers
    const hasFeature = computed(() => (featureName: string) => {
        return !!features.value[featureName]?.available;
    });

    const getFeature = computed(() => (featureName: string) => {
        return features.value[featureName];
    });

    const isPhysgun = computed(() => data.value?.addons?.some((addon) => addon.name === 'physgun') ?? false);
    const isPhysgunSharedHosting = computed(() => {
        if (!isPhysgun.value) return false;
        const host = window.location.hostname;
        // Production shared-hosting domain + the dev/test variant.
        // Mirrors the daemon-side gate in physgun.IsSharedHosting()
        // so panel dev hosts get the same UI features as prod.
        return host === 'gamecp.physgun.com' || host === 'physgun.panel.wisp.infraly.co' || host.endsWith('.test');
    });
    const hasFirewallProviders = computed(() => (data.value?.firewall?.providers?.length ?? 0) > 0);

    // Branding helpers
    const siteName = computed(() => branding.value?.name || 'Wisp');
    const siteLogo = computed(() => branding.value?.logo);
    const loginLogo = computed(() => branding.value?.login_logo);
    const favicon = computed(() => branding.value?.favicon);

    // Actions
    const markInitialized = () => {
        initialized.value = true;
    };

    const setSettings = (newSettings?: Settings) => {
        data.value = newSettings;
    };

    const updateSettings = (payload: Partial<Settings>) => {
        if (!data.value) return;

        const deepUpdate = (target: Record<string, any>, source: Record<string, any>) => {
            for (const key in source) {
                const value = source[key];

                if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                    if (!target[key] || typeof target[key] !== 'object') {
                        target[key] = {};
                    }
                    deepUpdate(target[key], value);
                } else {
                    target[key] = value;
                }
            }
        };

        deepUpdate(data.value as Record<string, any>, payload as Record<string, any>);
    };

    const clearSettings = () => {
        initialized.value = false;
        data.value = undefined;
    };

    // Initialize with empty features and addons if not provided
    const initializeDefaults = () => {
        if (!data.value) {
            data.value = {
                features: {},
                addons: [],
            };
        }
    };

    return {
        // State
        initialized,
        data,

        // Getters
        isInitialized,
        settings,
        branding,
        features,
        addons,
        eggSpecificTags,
        isSpecialTagSlug,
        whmcs,
        smtp,
        mailTheme,
        openGraph,
        injector,
        misc,
        pusher,
        defaultLocale,
        isExpired,
        subscriptionLocksAt,

        // Feature helpers
        hasFeature,
        getFeature,

        // Addons helpers
        isPhysgun,
        isPhysgunSharedHosting,

        // Firewall helpers
        hasFirewallProviders,

        // Branding helpers
        siteName,
        siteLogo,
        loginLogo,
        favicon,

        // Actions
        markInitialized,
        setSettings,
        updateSettings,
        clearSettings,
        initializeDefaults,
    };
});

// Export types for external use
export type SettingsStore = ReturnType<typeof useSettingsStore>;
